﻿import { IBathymetryState } from "./ChartModels";
import { IColorScale, InterpolationType } from "./ColorScale";

export type RGBAColor = [number, number, number, number];

export type ColorScaleMap = {
    values: number[],
    colors: RGBAColor[],
    extendAbove: RGBAColor | undefined,
    extendBelow: RGBAColor | undefined
};

export const DEFAULT_GRADIENT_COLORS = 200;
export const DEFAULT_COLOR: RGBAColor = [0, 0, 0, 0];

export enum ColorScaleSpaceIds {
    ECDIS = -1,
    Paper = -2,
    Gradient = -3,
    Parula = -4,
}

export const colorScales: IColorScale[] = [
    {
        colors: [
            RGBAtoARGB([115, 185, 142, 255]), RGBAtoARGB([115, 185, 142, 255]),
            RGBAtoARGB([123, 193, 241, 255]), RGBAtoARGB([123, 193, 241, 255]),
            RGBAtoARGB([185, 225, 222, 255]), RGBAtoARGB([185, 225, 222, 255]),
            RGBAtoARGB([216, 244, 225, 255]), RGBAtoARGB([216, 244, 225, 255])
        ],
        types: [InterpolationType.Fixed, InterpolationType.Fixed, InterpolationType.Fixed, InterpolationType.Fixed],
        hasFixedValues: false,
        name: 'ECDIS',
        spaceId: ColorScaleSpaceIds.ECDIS,
        displayCategory: 0,
        lookupKeys: [0]
    },
    {
        colors: [
            RGBAtoARGB([0x96, 0xd1, 0xb2, 0xff]), RGBAtoARGB([0x96, 0xd1, 0xb2, 0xff]),
            RGBAtoARGB([0x9b, 0xdb, 0xff, 0xff]), RGBAtoARGB([0x9b, 0xdb, 0xff, 0xff]),
            RGBAtoARGB([0xc8, 0xed, 0xff, 0xff]), RGBAtoARGB([0xc8, 0xed, 0xff, 0xff]),
            RGBAtoARGB([0xef, 0xf5, 0xff, 0xff]), RGBAtoARGB([0xef, 0xf5, 0xff, 0xff])
        ],
        types: [InterpolationType.Fixed, InterpolationType.Fixed, InterpolationType.Fixed, InterpolationType.Fixed],
        hasFixedValues: false,
        name: 'Paper',
        spaceId: ColorScaleSpaceIds.Paper,
        displayCategory: 0,
        lookupKeys: [0]
    },
    {
        colors: [
            RGBAtoARGB([0xff, 0xff, 0xff, 0]), RGBAtoARGB([0xff, 0xff, 0xff, 0xff]),
            RGBAtoARGB([0xff, 0xff, 0xff, 0xff]), RGBAtoARGB([0xb9, 0xca, 0xff, 0xff]),
            RGBAtoARGB([0xb9, 0xca, 0xff, 0xff]), RGBAtoARGB([0x4e, 0x62, 0x9d, 0xff]),
            RGBAtoARGB([0x4e, 0x62, 0x9d, 0xff]), RGBAtoARGB([0x17, 0x27, 0x58, 0xff]),
            RGBAtoARGB([0x17, 0x27, 0x58, 0xff]), RGBAtoARGB([0x0b, 0x12, 0x29, 0xff])
        ],
        types: [InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient],
        hasFixedValues: false,
        extendAbove: -1,
        extendBelow: -16051671,
        name: 'Gradient',
        spaceId: ColorScaleSpaceIds.Gradient
    },
    {
        colors: [
            RGBAtoARGB([249, 251, 14, 0]), RGBAtoARGB([249, 251, 14, 255]),
            RGBAtoARGB([249, 251, 14, 255]), RGBAtoARGB([252, 206, 46, 255]),
            RGBAtoARGB([252, 206, 46, 255]), RGBAtoARGB([225, 185, 82, 255]),
            RGBAtoARGB([225, 185, 82, 255]), RGBAtoARGB([165, 190, 107, 255]),
            RGBAtoARGB([165, 190, 107, 255]), RGBAtoARGB([89, 189, 140, 255]),
            RGBAtoARGB([89, 189, 140, 255]), RGBAtoARGB([21, 177, 180, 255]),
            RGBAtoARGB([21, 177, 180, 255]), RGBAtoARGB([7, 156, 207, 255]),
            RGBAtoARGB([7, 156, 207, 255]), RGBAtoARGB([18, 125, 216, 255]),
            RGBAtoARGB([18, 125, 216, 255]), RGBAtoARGB([15, 92, 221, 255]),
            RGBAtoARGB([15, 92, 221, 255]), RGBAtoARGB([53, 42, 135, 255])
        ],
        types: [InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient, InterpolationType.Gradient],
        hasFixedValues: false,
        extendAbove: 16382734,
        extendBelow: -13292921,
        name: 'Parula',
        spaceId: ColorScaleSpaceIds.Parula
    }
]

export const getColorScaleMap = (state: IBathymetryState) : ColorScaleMap | undefined => {

    let colorScale = state?.colorScales?.find(cs => cs.spaceId === state.spaceId);

    // Use default colorscale in case colorscale is not found (in Tracker the available colorscales changes depending on selected survey) 
    if (!colorScale)
        colorScale = state?.colorScales?.[0];

    if (!state || !colorScale) {
        return undefined;
    }

    let values = colorScale.values ? colorScale.values.slice() : [];
    let colors = colorScale.colors.slice().map(color => ARGBtoRGBA(color!));
    let types = colorScale.types ? colorScale.types.slice() : [];
    let above: RGBAColor | undefined = colorScale.extendAbove ? ARGBtoRGBA(colorScale.extendAbove) : undefined;
    let below: RGBAColor | undefined = colorScale.extendBelow ? ARGBtoRGBA(colorScale.extendBelow) : undefined;

    // case shallow < 0 may no longer be relevant since user can't select a shallow value >= 0 in UI anymore for ECDIS and PAPER
    if (colorScale.spaceId === -1 || colorScale.spaceId === -2) { // ECDIS or PAPER
        if (state.depthShallow < 0) {
            values.push(0);
        }
        values.push(state.depthShallow);
        values.push(state.depthDeep);

        if (state.depthShallow >= 0) {
            colors = colors.slice(2);
            types = types.slice(1);
        }

        // get above and below
        above = colors.at(0);
        below = colors.at(-1);

        // remove above and below
        colors = colors.slice(2, -2);
        types = types.slice(1, -1);
    }
    else if (state.depthShallow !== undefined && state.depthDeep !== undefined && values.length === 0) {
        for (let i = 0; i <= types.length; i++) {
            values.push(state.depthShallow + (i / types.length) * (state.depthDeep - state.depthShallow));
        }
    }

    let newColors: RGBAColor[] = [];
    let newValues: number[] = [];
    newValues.push(values[0]);
    for (let i = 0; i < types.length; i++) {
        if (types[i] === InterpolationType.Gradient || types[i] === InterpolationType.Rainbow) {
            newColors = newColors.concat(createScaleColors([colors[2 * i], colors[2 * i + 1]], DEFAULT_GRADIENT_COLORS, types[i]));
            newValues = newValues.concat(createScaleValues(values[i], values[i + 1], DEFAULT_GRADIENT_COLORS))
        }
        else if (types[i] === InterpolationType.Fixed) {
            newColors.push(colors[i * 2]);
            newValues.push(values[i + 1]);
        }
    }

    if (newValues.length == 0 || newColors.length == 0) {
        return undefined;
    }

    return {
        values: newValues,
        colors: newColors,
        extendAbove: above,
        extendBelow: below
    }
}

function createScaleColors(colors: [RGBAColor, RGBAColor], steps: number, type: InterpolationType) {
    const colorArray: RGBAColor[] = [];
    for (var colorIndex = 0; colorIndex < steps; colorIndex++) {
        if (type == InterpolationType.Gradient)
            colorArray[colorIndex] = interpolateBetween(colors[0], colors[1], (colorIndex) / (steps));
        else if (type == InterpolationType.Rainbow)
            colorArray[colorIndex] = interpolateBetweenRainbow(colors[0], colors[1], (colorIndex) / (steps));
    }

    return colorArray;
}

function createScaleValues(valueA: number, valueB: number, steps: number) {
    const valueArray: number[] = [];
    const diff = valueB - valueA;
    for (var index = 0; index < steps; index++) {
        valueArray[index] = valueA + index * (diff / steps);
    }

    return valueArray;
}

function interpolateBetween(c1: RGBAColor, c2: RGBAColor, lambda: number): RGBAColor {
    if (lambda < 0 || lambda > 1) {
        throw new Error("lambda");
    }

    return [
        Math.floor(c1[0] + (c2[0] - c1[0]) * lambda),
        Math.floor(c1[1] + (c2[1] - c1[1]) * lambda),
        Math.floor(c1[2] + (c2[2] - c1[2]) * lambda),
        Math.floor(c1[3] + (c2[3] - c1[3]) * lambda),
    ]
}

function interpolateBetweenRainbow(c1: RGBAColor, c2: RGBAColor, lambda: number): RGBAColor {
    const c1HSV = RGBtoHSV(c1[0], c1[1], c1[2],);
    const c2HSV = RGBtoHSV(c2[0], c2[1], c2[2]);

    const hMin = Math.min(c1HSV.h!, c2HSV.h!);
    const hMax = Math.max(c1HSV.h!, c2HSV.h!);
    const hLong = Math.max(hMax - hMin, Math.abs(hMin + (1 - hMax)))
    const lowToHigh = c1HSV.h! < c2HSV.h! ? 1 : -1;
    const firstOrRemainder = hMax - hMin > hMin + (1 - hMax) ? 1 : -1

    const hsvInterpolation = {
        h: ((c1HSV.h! + lowToHigh * firstOrRemainder * hLong * lambda) + 1) % 1,
        s: c1HSV.s + (c2HSV.s - c1HSV.s) * lambda,
        v: c1HSV.v + (c2HSV.v - c1HSV.v) * lambda,
    }

    const rgb = HSVtoRGB(hsvInterpolation.h, hsvInterpolation.s, hsvInterpolation.v);

    return [rgb.r, rgb.g, rgb.b, Math.floor(c1[3] + (c2[3] - c1[3]) * lambda)];
}

function RGBtoHSV(r: number, g: number, b: number) {
    var max = Math.max(r, g, b), min = Math.min(r, g, b),
        d = max - min,
        h,
        s = (max === 0 ? 0 : d / max),
        v = max / 255;

    switch (max) {
        case min: h = 0; break;
        case r: h = (g - b) + d * (g < b ? 6 : 0); h /= 6 * d; break;
        case g: h = (b - r) + d * 2; h /= 6 * d; break;
        case b: h = (r - g) + d * 4; h /= 6 * d; break;
    }

    return {
        h: h,
        s: s,
        v: v
    };
}

function HSVtoRGB(h: number, s: number, v: number) {
    let r: number, g: number, b: number, i, f, p, q, t;

    i = Math.floor(h * 6);
    f = h * 6 - i;
    p = v * (1 - s);
    q = v * (1 - f * s);
    t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0:
            r = v; g = t; b = p; break;
        case 1:
            r = q; g = v; b = p; break;
        case 2:
            r = p; g = v; b = t; break;
        case 3:
            r = p; g = q; b = v; break;
        case 4:
            r = t; g = p; b = v; break;
        case 5:
            r = v; g = p; b = q; break;
    }
    return {
        r: Math.round(r! * 255),
        g: Math.round(g! * 255),
        b: Math.round(b! * 255)
    };
}


// TODO: move?
export function RGBAtoARGB(rgba: RGBAColor): number {
    var r = rgba[0] & 0xFF;
    var g = rgba[1] & 0xFF;
    var b = rgba[2] & 0xFF;
    var a = (rgba[3]) & 0xFF; // removed 255 * rgba[3]

    return (a << 24) + (r << 16) + (g << 8) + (b);
}

export function ARGBtoRGBA(argb: number): RGBAColor {
    return [
        argb >> 16 & 0xFF,
        argb >> 8 & 0xFF,
        argb & 0xFF,
        (argb >> 24 & 0xFF), // removed / 255
    ]
}